
import React, { useEffect, useRef } from 'react';

interface StepProps {
  number: number;
  title: string;
  description: string;
  delay?: number;
}

const Step = ({ number, title, description, delay = 0 }: StepProps) => {
  const stepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('reveal-visible');
            }, delay);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (stepRef.current) {
      observer.observe(stepRef.current);
    }

    return () => {
      if (stepRef.current) {
        observer.unobserve(stepRef.current);
      }
    };
  }, [delay]);

  return (
    <div ref={stepRef} className="reveal flex flex-col items-center md:items-start">
      <div className="flex items-center justify-center h-16 w-16 rounded-full bg-ocean-600 text-white font-bold text-2xl mb-4">
        {number}
      </div>
      <h3 className="text-xl font-bold text-ocean-800 mb-2">{title}</h3>
      <p className="text-center md:text-left text-ocean-600">{description}</p>
    </div>
  );
};

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-ocean-800 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-ocean-600 max-w-2xl mx-auto">
            Create professional dive quotes in just three simple steps
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <Step 
            number={1} 
            title="Create" 
            description="Enter your dive shop details, add your products, services, and pricing options to the quote builder."
            delay={0}
          />
          <Step 
            number={2} 
            title="Customize" 
            description="Personalize your quote with your branding, select templates, and tailor it to your client's specific needs."
            delay={200}
          />
          <Step 
            number={3} 
            title="Generate PDF" 
            description="Export your quote as a professional PDF document ready to share with your clients."
            delay={400}
          />
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
